import {
    CheckCircleFilled,
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import {
    AutoComplete,
    Button,
    Form,
    Input,
    message,
    Modal,
    Pagination,
    Space,
    Tabs,
    Tag,
} from 'antd';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { createMinimaxVideo, getNextHailuoAccount } from '../services/api';
import { useAppSelector } from '../store/hooks';

const { TextArea } = Input;
const { TabPane } = Tabs;
const API_URL = process.env.REACT_APP_API_URL;

const cinematicPresets = [
    {
        label: 'Debut',
        value: 'Truck left,Push in,Pan right',
        video: '/video/compressed_debut.mp4',
    },
    {
        label: 'Freedom',
        value: 'Push out,Pedestal up,Tilt down',
        video: '/video/compressed_freedom.mp4',
    },
    {
        label: 'Right circling',
        value: 'Truck right,Pan left,Tracking shot',
        video: '/video/rightcircling_360w_optimized_2.mp4',
    },
    {
        label: 'Left circling',
        value: 'Truck left,Pan right,Tracking shot',
        video: '/video/leftcircling_360w_optimized_2.mp4',
    },
    {
        label: 'Upward tilt',
        value: 'Push in,Pedestal up',
        video: '/video/upwardtilt_360w_optimized_2.mp4',
    },
    {
        label: 'Left walking',
        value: 'Truck left,Tracking shot',
        video: '/video/leftwalking_360w_optimized_2.mp4',
    },
    {
        label: 'Right walking',
        value: 'Truck right,Tracking shot',
        video: '/video/rightwalking_360w_optimized_2.mp4',
    },
    {
        label: 'Downward tilt',
        value: 'Pedestal down,Tilt up',
        video: '/video/downwardtilt_360w_optimized_2.mp4',
    },
    {
        label: 'Stage left',
        value: 'Pan left,Zoom in',
        video: '/video/stageleft_360w_optimized_2.mp4',
    },
    {
        label: 'Stage right',
        value: 'Pan right,Zoom in',
        video: '/video/stageright_360w_optimized_2.mp4',
    },
    {
        label: 'Scenic shot',
        value: 'Truck left,Pedestal up',
        video: '/video/scenicshot_360w_optimized_2.mp4',
    },
];

const freeMotionOptions = [
    {
        groupLabel: 'Truck',
        options: [
            {
                label: 'Truck left',
                video: '/video/truckleft_360w_optimized_2.mp4',
            },
            {
                label: 'Truck right',
                video: '/video/truckright_360w_optimized_2.mp4',
            },
        ],
    },
    {
        groupLabel: 'Pan',
        options: [
            { label: 'Pan left', video: '/video/panleft_360w_optimized_2.mp4' },
            {
                label: 'Pan right',
                video: '/video/panright_360w_optimized_2.mp4',
            },
        ],
    },
    {
        groupLabel: 'Push/Pull',
        options: [
            { label: 'Push in', video: '/video/pushin_360w_optimized_2.mp4' },
            { label: 'Pull out', video: '/video/pullout_360w_optimized_2.mp4' },
        ],
    },
    {
        groupLabel: 'Pedestal',
        options: [
            {
                label: 'Pedestal up',
                video: '/video/pedestalup_360w_optimized_2.mp4',
            },
            {
                label: 'Pedestal down',
                video: '/video/pedestaldown_360w_optimized_2.mp4',
            },
        ],
    },
    {
        groupLabel: 'Tilt',
        options: [
            { label: 'Tilt up', video: '/video/tiltup_360w_optimized_2.mp4' },
            {
                label: 'Tilt down',
                video: '/video/tiltdown_360w_optimized_2.mp4',
            },
        ],
    },
    {
        groupLabel: 'Zoom',
        options: [
            { label: 'Zoom in', video: '/video/zoomin_360w_optimized_2.mp4' },
            { label: 'Zoom out', video: '/video/zoomout_360w_optimized_2.mp4' },
        ],
    },
    {
        groupLabel: 'Shake',
        options: [
            { label: 'Shake', video: '/video/shake_360w_optimized_2.mp4' },
        ],
    },
    {
        groupLabel: 'Tracking shot',
        options: [
            {
                label: 'Tracking shot',
                video: '/video/trackingshot_360w_optimized_2.mp4',
            },
        ],
    },
    {
        groupLabel: 'Static shot',
        options: [
            {
                label: 'Static shot',
                video: '/video/staticshot_360w_optimized_2.mp4',
            },
        ],
    },
];

const MotionGroupCard = ({
    group,
    selectedMotions,
    setSelectedMotions,
    setDescription,
    setCameraMovement,
}) => {
    const [hoveredVideo, setHoveredVideo] = useState(group.options[0].video);

    return (
        <div
            style={{
                border: '1px solid #ccc',
                borderRadius: 8,
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: '0.3s',
            }}
        >
            <video
                src={hoveredVideo}
                style={{ width: '100%', height: 100, objectFit: 'cover' }}
                muted
                loop
                playsInline
                autoPlay
                disablePictureInPicture
                controls={false}
            />
            <div style={{ display: 'flex' }}>
                {group.options.map((motion) => {
                    const isSelected = selectedMotions.includes(motion.label);
                    return (
                        <Button
                            key={motion.label}
                            type={isSelected ? 'primary' : 'default'}
                            onClick={() => {
                                const groupMotionLabels = group.options.map(
                                    (opt) => opt.label,
                                );
                                let updatedMotions = selectedMotions.filter(
                                    (m) => !groupMotionLabels.includes(m),
                                );

                                const isAlreadySelected =
                                    selectedMotions.includes(motion.label);
                                if (!isAlreadySelected) {
                                    if (updatedMotions.length >= 3) {
                                        toast.error(
                                            'Chỉ được chọn tối đa 3 chuyển động',
                                        );
                                        return;
                                    }
                                    updatedMotions.push(motion.label);
                                }

                                setSelectedMotions(updatedMotions);

                                setDescription((prevDesc) => {
                                    const matches =
                                        prevDesc.match(/\[[^\]]*\]/g) || [];
                                    let cinematic = '';
                                    let free = '';

                                    if (
                                        matches.length > 0 &&
                                        cinematicPresets.some(
                                            (preset) =>
                                                preset.value ===
                                                matches[0].slice(1, -1).trim(),
                                        )
                                    ) {
                                        cinematic = matches[0];
                                    }
                                    const otherText = prevDesc
                                        .replace(/\[[^\]]*\]/g, '')
                                        .trim();

                                    free =
                                        updatedMotions.length > 0
                                            ? `[${updatedMotions.join(', ')}]`
                                            : '';

                                    return [otherText, cinematic, free]
                                        .filter(Boolean)
                                        .join(' ')
                                        .trim();
                                });

                                setCameraMovement(updatedMotions.join(', '));
                            }}
                            onMouseEnter={() => setHoveredVideo(motion.video)}
                            onMouseLeave={() =>
                                setHoveredVideo(group.options[0].video)
                            }
                            style={{ flex: 1, borderRadius: 0 }}
                        >
                            {motion.label}
                        </Button>
                    );
                })}
            </div>
        </div>
    );
};

const PromptForm = ({
    provider,
    setDescription,
    reloadKey,
    selectedTitle,
    setSelectedTitle,
    minimaxMode,
    setReloadKey,
    onReload,
}) => {
    const { videoSelected } = useAppSelector((state) => state.video);

    const [form] = Form.useForm();
    const [topics, setTopics] = useState([]);
    const [accountId, setAccountId] = useState(null);
    const [loadingSuggest, setLoadingSuggest] = useState(false);
    const [loadingGenerate, setLoadingGenerate] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [userPrompt, setUserPrompt] = useState('');
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingTopic, setEditingTopic] = useState(null);
    const [editingDescription, setEditingDescription] = useState('');
    const [editingKeywords, setEditingKeywords] = useState('');
    const [editingTitle, setEditingTitle] = useState('');
    const [promptCount, setPromptCount] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const [cameraModalVisible, setCameraModalVisible] = useState(false);
    const [cameraMovement, setCameraMovement] = useState('');
    const [selectedMotions, setSelectedMotions] = useState([]);
    const [selectedPromptIndex, setSelectedPromptIndex] = useState(null);

    const [promptModalVisible, setPromptModalVisible] = useState(false);
    const [generatedPrompts, setGeneratedPrompts] = useState([]);

    const [bulkCameraModalVisible, setBulkCameraModalVisible] = useState(false);
    const [bulkCameraMovement, setBulkCameraMovement] = useState('');

    const paginatedPrompts = generatedPrompts.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize,
    );

    const titleInputRef = useRef(null);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('account_web') || 'null');
        if (user?.id) {
            setAccountId(user.id);
        } else {
            setAccountId(null);
        }
    }, [reloadKey]);

    useEffect(() => {
        if (accountId) loadTopics();
    }, [accountId, provider]);

    useEffect(() => {
        if (modalOpen && titleInputRef.current) {
            setTimeout(() => titleInputRef.current.focus(), 100);
        }
    }, [modalOpen]);

    useEffect(() => {
        if (videoSelected && videoSelected.title) {
            form.resetFields();
            form.setFieldsValue({
                title: videoSelected.title,
                description: videoSelected.description,
            });
            setSelectedTitle(videoSelected.title);
            if (setDescription) {
                setDescription(videoSelected.description);
            }
        }
    }, [videoSelected]);

    const loadTopics = async () => {
        try {
            const res = await fetch(
                `${API_URL}/prompts/titles?accountId=${accountId}`,
            );
            const data = await res.json();

            const uniqueTitles = Array.from(new Set(data));

            setTopics(uniqueTitles);
        } catch (err) {
            console.error('Lỗi load topics:', err);
        }
    };

    const handleSuggest = async () => {
        if (!newTitle || !userPrompt)
            return message.warning('Vui lòng nhập đủ thông tin.');

        setLoadingSuggest(true);
        try {
            const finalPrompt = `${userPrompt.trim()}

Kết quả trả về dưới dạng JSON:
{
  "description": "...",
  "keywords": "keyword 1, keyword 2, ..."
}`;

            const res = await fetch(`${API_URL}/prompts/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    title: newTitle,
                    prompt: finalPrompt,
                    accountId,
                    provider,
                }),
            });

            const data = await res.json();

            form.setFieldsValue({
                title: newTitle,
                description: data.description,
                keywords: data.keywords,
            });

            setModalOpen(false);
            setNewTitle('');
            setUserPrompt('');
            await loadTopics();
            setSelectedTitle(newTitle);
            form.setFieldsValue({ title: newTitle });
        } catch (err) {
            console.error('Lỗi gợi ý:', err);
            message.error('Không lấy được dữ liệu gợi ý.');
        } finally {
            setLoadingSuggest(false);
        }
    };

    const handleSelectTitle = (value) => {
        setSelectedTitle(value);
        form.setFieldsValue({ title: value });
    };

    const handleFetchOldPrompt = async (title) => {
        const chosenTitle = title || selectedTitle;
        if (!chosenTitle) return message.warning('Vui lòng chọn một chủ đề.');
        setLoadingSuggest(true);
        try {
            const res = await fetch(
                `${API_URL}/prompts/by-title?title=${encodeURIComponent(
                    chosenTitle,
                )}&accountId=${accountId}`,
            );
            if (!res.ok) throw new Error('Không lấy được dữ liệu từ DB');

            const data = await res.json();

            form.setFieldsValue({
                description: data.description || '',
                keywords: data.keywords || '',
            });
            setDescription(data.description || '');
        } catch (err) {
            console.error('Lỗi lấy prompt từ DB:', err);
            message.error('Không lấy được dữ liệu từ CSDL.');
        } finally {
            setLoadingSuggest(false);
        }
    };

    const onFinish = async (values) => {
        const title =
            typeof values.title === 'string'
                ? values.title.trim()
                : values.title?.[0];

        const payload = {
            ...values,
            title,
            provider,
            accountId,
        };

        if (minimaxMode === 'text') {
            payload.quantity = promptCount;
            payload.description = null;
        }

        setLoadingGenerate(true);
        try {
            const endpoint =
                minimaxMode === 'text'
                    ? `${API_URL}/prompts/generate-text`
                    : `${API_URL}/prompts/generate`;

            const res = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            const data = await res.json();

            if (minimaxMode === 'text') {
                setGeneratedPrompts(data.prompts || []);
                setPromptModalVisible(true);
            } else {
                setDescription(data.prompt);
            }

            await loadTopics();
            setSelectedTitle(title);
        } catch (err) {
            console.error('Lỗi tạo prompt:', err);
            message.error('Không tạo được prompt.');
        } finally {
            setLoadingGenerate(false);
        }
    };

    const handleDeleteTitle = async (title) => {
        const user = JSON.parse(localStorage.getItem('account_web') || 'null');
        if (!user?.id) return;

        try {
            await fetch(
                `${API_URL}/prompts?title=${encodeURIComponent(
                    title,
                )}&accountId=${user.id}`,
                {
                    method: 'DELETE',
                },
            );

            message.success('Đã xoá chủ đề.');
            setTopics((prev) => prev.filter((t) => t !== title));
            if (selectedTitle === title) {
                setSelectedTitle('');
                form.setFieldValue('title', '');
            }
        } catch (err) {
            console.error('Lỗi xoá chủ đề:', err);
            message.error('Không thể xoá chủ đề.');
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setNewTitle('');
        setUserPrompt('');
    };
    const openEditModal = async (topic) => {
        try {
            const res = await fetch(
                `${API_URL}/prompts/by-title?title=${encodeURIComponent(
                    topic,
                )}&accountId=${accountId}`,
            );
            const data = await res.json();

            setEditingTopic(topic);
            setEditingTitle(topic);
            setEditingDescription(data.description || '');
            setEditingKeywords(data.keywords || '');
            setEditModalOpen(true);
        } catch (err) {
            message.error('Không lấy được dữ liệu để sửa.');
            console.error(err);
        }
    };
    const handleUpdateTopic = async () => {
        if (!editingTitle.trim()) {
            return message.warning('Tiêu đề không được để trống.');
        }

        try {
            await fetch(`${API_URL}/prompts/update-full`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    oldTitle: editingTopic,
                    newTitle: editingTitle.trim(),
                    description: editingDescription,
                    keywords: editingKeywords,
                    accountId,
                    provider,
                }),
            });

            message.success('Đã cập nhật chủ đề.');
            setEditModalOpen(false);
            if (selectedTitle === editingTopic) {
                setSelectedTitle(editingTitle.trim());
                form.setFieldsValue({
                    title: editingTitle.trim(),
                    description: editingDescription,
                    keywords: editingKeywords,
                });
                setDescription(editingDescription);
            }
            await loadTopics();
        } catch (err) {
            console.error('Lỗi cập nhật:', err);
            message.error('Không thể cập nhật chủ đề.');
        }
    };
    const handleConfirmPrompts = async () => {
        const ownerId = accountId;
        const title = selectedTitle;

        for (const prompt of generatedPrompts) {
            try {
                const selectedAcc = await getNextHailuoAccount(ownerId);
                const accId = selectedAcc.accountId;

                await createMinimaxVideo(
                    accId,
                    null,
                    prompt,
                    title,
                    ownerId,
                    null,
                    'T2V-01-Director',
                    null,
                    'text',
                );
            } catch (err) {
                console.error('Lỗi tạo videoo :', err);
            }
        }
        toast.success('Đã gửi yêu cầu tạo video từ các prompt!');
        setPromptModalVisible(false);

        if (onReload) onReload();
    };

    const handleApplyBulkCamera = (selectedMotion, isRemoving = false) => {
        const updatedPrompts = generatedPrompts.map((prompt) => {
            if (isRemoving) {
                return prompt.replace(/\[(.*?)\]/g, '').trim();
            } else {
                const cleaned = prompt.replace(/\[(.*?)\]/g, '').trim();
                return `${cleaned} [${selectedMotion}]`.trim();
            }
        });

        setGeneratedPrompts(updatedPrompts);
        setBulkCameraModalVisible(false);
        setBulkCameraMovement(isRemoving ? '' : selectedMotion);
    };

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <h3 style={{ margin: 0 }}> Tạo Prompt </h3>
                <Button
                    onClick={() => {
                        form.resetFields();
                        setSelectedTitle('');
                    }}
                >
                    Clear
                </Button>
            </div>

            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Form.Item label="Chủ đề" name="title">
                    <Space.Compact style={{ width: '100%' }}>
                        <AutoComplete
                            style={{ flex: 1 }}
                            placeholder="Nhập hoặc chọn chủ đề"
                            value={selectedTitle}
                            onChange={(value) => {
                                setSelectedTitle(value);
                                form.setFieldsValue({ title: value });

                                const matched = topics.includes(value);
                                if (matched) {
                                    handleFetchOldPrompt(value);
                                }
                            }}
                            disabled={!accountId}
                            options={topics.map((t) => ({
                                value: t,
                                label: (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <span>{t}</span>
                                        <Space>
                                            <Button
                                                size="small"
                                                danger
                                                icon={<DeleteOutlined />}
                                                type="text"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    Modal.confirm({
                                                        title: 'Xác nhận xoá',
                                                        content: `Bạn chắc chắn muốn xoá chủ đề "${t}"?`,
                                                        okText: 'Xoá',
                                                        cancelText: 'Huỷ',
                                                        onOk: () =>
                                                            handleDeleteTitle(
                                                                t,
                                                            ),
                                                    });
                                                }}
                                            />
                                        </Space>
                                    </div>
                                ),
                            }))}
                        >
                            <Input />
                        </AutoComplete>
                    </Space.Compact>

                    {minimaxMode !== 'text' && (
                        <div style={{ marginTop: 8, textAlign: 'center' }}>
                            <Button
                                icon={<PlusOutlined />}
                                onClick={() => setModalOpen(true)}
                                type="dashed"
                            >
                                Thêm chủ đề bằng AI
                            </Button>
                        </div>
                    )}
                </Form.Item>

                {minimaxMode !== 'text' && (
                    <Form.Item label="Mô tả chi tiết" name="description">
                        <Input.TextArea rows={8} />
                    </Form.Item>
                )}

                <Form.Item label="Từ khoá" name="keywords">
                    <TextArea rows={2} placeholder="Cách nhau bằng dấu phẩy" />
                </Form.Item>
                {minimaxMode === 'text' && (
                    <Form.Item
                        label="Số lượng Prompt"
                        style={{ maxWidth: 200 }}
                    >
                        <Input
                            type="number"
                            value={promptCount}
                            onChange={(e) => {
                                const val = Number(e.target.value);
                                if (!isNaN(val)) {
                                    setPromptCount(val);
                                    if (val > 150) {
                                        message.warning(
                                            'Số lượng tối đa là 150 Prompt.',
                                        );
                                    }
                                }
                            }}
                            min={1}
                            max={150}
                            style={{
                                width: 80,
                                textAlign: 'center',
                                MozAppearance: 'textfield',
                            }}
                            onWheel={(e) => e.target.blur()}
                            size="small"
                        />
                    </Form.Item>
                )}

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        block
                        loading={loadingGenerate}
                    >
                        Tạo Prompt
                    </Button>
                </Form.Item>
            </Form>

            {generatedPrompts.length > 0 && minimaxMode === 'text' && (
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => setPromptModalVisible(true)}
                        type="default"
                    >
                        Danh sách Prompt đã tạo ({generatedPrompts.length})
                    </Button>
                </div>
            )}

            <Modal
                title="Thêm chủ đề mới"
                open={modalOpen}
                onCancel={handleCloseModal}
                onOk={handleSuggest}
                okText="Lấy gợi ý mô tả & từ khóa"
                confirmLoading={loadingSuggest}
            >
                <Input
                    ref={titleInputRef}
                    placeholder="Nhập tiêu đề chủ đề"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    style={{ marginBottom: 12 }}
                />
                <TextArea
                    placeholder="Nhập yêu cầu để từ Chủ Đề AI sẽ tạo ra Mô Tả Chi Tiết và Từ Khóa"
                    rows={4}
                    value={userPrompt}
                    onChange={(e) => setUserPrompt(e.target.value)}
                />
                <p style={{ color: '#999', fontSize: 13, marginTop: 8 }}>
                    * Sau khi lấy gợi ý, bạn có thể chỉnh sửa mô tả và từ khoá
                    trước khi tạo Prompt.
                </p>
            </Modal>
            <Modal
                title={`Sửa chủ đề: ${editingTopic}`}
                open={editModalOpen}
                onCancel={() => setEditModalOpen(false)}
                onOk={handleUpdateTopic}
                okText="Lưu"
            >
                <p style={{ color: '#999', fontSize: 13, marginBottom: 16 }}>
                    * Khi sửa "tiêu đề", bạn cần sửa thêm "mô tả" hoặc "từ khoá"
                    để có thể tạo mô tả video mới hoàn toàn.
                </p>

                <Form layout="vertical">
                    <Form.Item label="Tiêu đề mới">
                        <Input
                            value={editingTitle}
                            onChange={(e) => setEditingTitle(e.target.value)}
                            placeholder="Tiêu đề chủ đề"
                        />
                    </Form.Item>
                    <Form.Item label="Mô tả">
                        <TextArea
                            rows={3}
                            value={editingDescription}
                            onChange={(e) =>
                                setEditingDescription(e.target.value)
                            }
                        />
                    </Form.Item>
                    <Form.Item label="Từ khoá">
                        <Input
                            value={editingKeywords}
                            onChange={(e) => setEditingKeywords(e.target.value)}
                            placeholder="Cách nhau bằng dấu phẩy"
                        />
                    </Form.Item>
                </Form>
            </Modal>
            <Modal
                title="Danh sách Prompt đã tạo"
                open={promptModalVisible}
                onCancel={() => setPromptModalVisible(false)}
                footer={[
                    <Button
                        key="bulk-camera"
                        onClick={() => setBulkCameraModalVisible(true)}
                        style={{ marginRight: 8 }}
                    >
                        🎥 Chọn góc quay cho tất cả
                    </Button>,
                    <Button
                        key="cancel"
                        onClick={() => setPromptModalVisible(false)}
                    >
                        Hủy
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        onClick={handleConfirmPrompts}
                    >
                        Tạo Video
                    </Button>,
                ]}
                width={900}
            >
                {generatedPrompts
                    .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                    .map((prompt, index) => {
                        const realIndex = (currentPage - 1) * pageSize + index;

                        return (
                            <div
                                key={realIndex}
                                style={{
                                    display: 'flex',
                                    gap: 8,
                                    alignItems: 'flex-start',
                                    marginBottom: 10,
                                }}
                            >
                                <TextArea
                                    rows={6}
                                    style={{ flex: 1 }}
                                    value={generatedPrompts[realIndex]}
                                    onChange={(e) => {
                                        const newPrompts = [
                                            ...generatedPrompts,
                                        ];
                                        newPrompts[realIndex] = e.target.value;
                                        setGeneratedPrompts(newPrompts);
                                    }}
                                />

                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 8,
                                    }}
                                >
                                    <Button
                                        type="default"
                                        onClick={() => {
                                            setSelectedPromptIndex(realIndex);
                                            const currentPrompt =
                                                generatedPrompts[realIndex];
                                            setDescription(currentPrompt);

                                            const match =
                                                currentPrompt.match(
                                                    /\[(.*?)\]/,
                                                );
                                            if (match) {
                                                const motion = match[1].trim();
                                                setCameraMovement(motion);
                                                if (
                                                    !cinematicPresets.some(
                                                        (p) =>
                                                            p.value === motion,
                                                    )
                                                ) {
                                                    setSelectedMotions(
                                                        motion
                                                            .split(',')
                                                            .map((s) =>
                                                                s.trim(),
                                                            ),
                                                    );
                                                } else {
                                                    setSelectedMotions([]);
                                                }
                                            } else {
                                                setCameraMovement('');
                                                setSelectedMotions([]);
                                            }

                                            setCameraModalVisible(true);
                                        }}
                                    >
                                        🎥 Chọn góc quay
                                    </Button>

                                    <Button
                                        danger
                                        onClick={async () => {
                                            try {
                                                const accountWeb = JSON.parse(
                                                    localStorage.getItem(
                                                        'account_web',
                                                    ),
                                                );
                                                const accountId =
                                                    accountWeb?.id;

                                                if (!accountId) {
                                                    message.error(
                                                        'Không tìm thấy tài khoản.',
                                                    );
                                                    return;
                                                }

                                                const title =
                                                    generatedPrompts[realIndex];

                                                await fetch(
                                                    `${API_URL}/prompts/delete`,
                                                    {
                                                        method: 'POST',
                                                        headers: {
                                                            'Content-Type':
                                                                'application/json',
                                                        },
                                                        body: JSON.stringify({
                                                            accountId,
                                                            title,
                                                        }),
                                                    },
                                                );

                                                message.success(
                                                    'Đã xoá prompt thành công',
                                                );

                                                const updated = [
                                                    ...generatedPrompts,
                                                ];
                                                updated.splice(realIndex, 1);
                                                setGeneratedPrompts(updated);
                                            } catch (err) {
                                                message.error(
                                                    'Xoá prompt thất bại.',
                                                );
                                            }
                                        }}
                                    >
                                        🗑️
                                    </Button>
                                </div>
                            </div>
                        );
                    })}

                <Pagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={generatedPrompts.length}
                    onChange={(page) => setCurrentPage(page)}
                    style={{ textAlign: 'center', marginTop: 16 }}
                />
            </Modal>
            <Modal
                open={cameraModalVisible}
                onCancel={() => setCameraModalVisible(false)}
                footer={null}
                title="Chọn góc quay"
                width={800}
            >
                <Tabs defaultActiveKey="cinematic">
                    <TabPane tab="Cinematic shots" key="cinematic">
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns:
                                    'repeat(auto-fill, minmax(160px, 1fr))',
                                gap: '16px',
                            }}
                        >
                            {cinematicPresets.map((item) => {
                                const currentPrompt =
                                    generatedPrompts[selectedPromptIndex] || '';
                                const motionFromDesc = currentPrompt
                                    .match(/\[(.*?)\]/)?.[1]
                                    ?.trim();
                                const isSelected =
                                    motionFromDesc === item.value;

                                return (
                                    <div
                                        key={item.label}
                                        onClick={() => {
                                            const motion = item.value;
                                            const currentMatch =
                                                currentPrompt.match(
                                                    /\[(.*?)\]/,
                                                );
                                            const currentMotion =
                                                currentMatch?.[1]?.trim();
                                            const isSame =
                                                currentMotion === motion;

                                            let newPrompt = currentPrompt;

                                            if (isSame) {
                                                newPrompt = currentPrompt
                                                    .replace(/\[(.*?)\]/g, '')
                                                    .trim();
                                                setCameraMovement('');
                                            } else {
                                                const cleaned = currentPrompt
                                                    .replace(/\[(.*?)\]/g, '')
                                                    .trim();
                                                newPrompt =
                                                    `${cleaned} [${motion}]`.trim();
                                                setCameraMovement(motion);
                                            }

                                            const updatedPrompts = [
                                                ...generatedPrompts,
                                            ];
                                            updatedPrompts[
                                                selectedPromptIndex
                                            ] = newPrompt;
                                            setGeneratedPrompts(updatedPrompts);

                                            setCameraModalVisible(false);
                                        }}
                                        style={{
                                            position: 'relative',
                                            cursor: 'pointer',
                                            border: isSelected
                                                ? '2px solid #1677ff'
                                                : '1px solid #ccc',
                                            backgroundColor: isSelected
                                                ? '#e6f4ff'
                                                : 'transparent',
                                            borderRadius: 8,
                                            overflow: 'hidden',
                                            boxShadow: isSelected
                                                ? '0 0 8px 2px rgba(22, 119, 255, 0.3)'
                                                : '0 2px 8px rgba(0,0,0,0.1)',
                                            transform: isSelected
                                                ? 'scale(1.02)'
                                                : 'none',
                                            transition: 'all 0.3s ease-in-out',
                                        }}
                                    >
                                        {isSelected && (
                                            <CheckCircleFilled
                                                style={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    right: 8,
                                                    color: '#1677ff',
                                                    fontSize: 20,
                                                    zIndex: 2,
                                                }}
                                            />
                                        )}
                                        <video
                                            src={item.video}
                                            style={{
                                                width: '100%',
                                                height: 100,
                                                objectFit: 'cover',
                                            }}
                                            muted
                                            loop
                                            playsInline
                                            autoPlay
                                            disablePictureInPicture
                                            controls={false}
                                        />
                                        <div
                                            style={{
                                                padding: '8px',
                                                textAlign: 'center',
                                                fontWeight: 500,
                                                color: isSelected
                                                    ? '#1677ff'
                                                    : undefined,
                                            }}
                                        >
                                            {item.label}
                                            {isSelected && (
                                                <div
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    Đã chọn
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </TabPane>

                    <TabPane tab="Free selection" key="free" disabled>
                        <div style={{ marginBottom: 12 }}>
                            <strong>
                                Đã chọn ({selectedMotions.length}/3):{' '}
                            </strong>
                            {selectedMotions.map((motion, index) => (
                                <Tag
                                    key={index}
                                    closable
                                    onClose={() =>
                                        setSelectedMotions(
                                            selectedMotions.filter(
                                                (m) => m !== motion,
                                            ),
                                        )
                                    }
                                >
                                    {motion}
                                </Tag>
                            ))}
                        </div>

                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns:
                                    'repeat(auto-fill, minmax(160px, 1fr))',
                                gap: 16,
                            }}
                        >
                            {freeMotionOptions.map((group, idx) => (
                                <MotionGroupCard
                                    key={idx}
                                    group={group}
                                    selectedMotions={selectedMotions}
                                    setSelectedMotions={setSelectedMotions}
                                    setDescription={(newDesc) => {
                                        const updated = [...generatedPrompts];
                                        updated[selectedPromptIndex] = newDesc;
                                        setGeneratedPrompts(updated);
                                    }}
                                    setCameraMovement={setCameraMovement}
                                    currentPrompt={
                                        generatedPrompts[selectedPromptIndex]
                                    }
                                />
                            ))}
                        </div>
                    </TabPane>
                </Tabs>
            </Modal>
            <Modal
                open={bulkCameraModalVisible}
                onCancel={() => setBulkCameraModalVisible(false)}
                footer={null}
                title="Chọn góc quay cho tất cả prompt"
                width={800}
            >
                <Tabs defaultActiveKey="cinematic">
                    <TabPane tab="Cinematic shots" key="cinematic">
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns:
                                    'repeat(auto-fill, minmax(160px, 1fr))',
                                gap: '16px',
                            }}
                        >
                            {cinematicPresets.map((item) => {
                                const isSelected =
                                    bulkCameraMovement === item.value;

                                return (
                                    <div
                                        key={item.label}
                                        onClick={() => {
                                            const isSame =
                                                bulkCameraMovement ===
                                                item.value;
                                            handleApplyBulkCamera(
                                                item.value,
                                                isSame,
                                            );
                                        }}
                                        style={{
                                            position: 'relative',
                                            cursor: 'pointer',
                                            border: isSelected
                                                ? '2px solid #1677ff'
                                                : '1px solid #ccc',
                                            backgroundColor: isSelected
                                                ? '#e6f4ff'
                                                : 'transparent',
                                            borderRadius: 8,
                                            overflow: 'hidden',
                                            boxShadow: isSelected
                                                ? '0 0 8px 2px rgba(22, 119, 255, 0.3)'
                                                : '0 2px 8px rgba(0,0,0,0.1)',
                                            transform: isSelected
                                                ? 'scale(1.02)'
                                                : 'none',
                                            transition: 'all 0.3s ease-in-out',
                                        }}
                                    >
                                        {isSelected && (
                                            <CheckCircleFilled
                                                style={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    right: 8,
                                                    color: '#1677ff',
                                                    fontSize: 20,
                                                    zIndex: 2,
                                                }}
                                            />
                                        )}
                                        <video
                                            src={item.video}
                                            style={{
                                                width: '100%',
                                                height: 100,
                                                objectFit: 'cover',
                                            }}
                                            muted
                                            loop
                                            playsInline
                                            autoPlay
                                            disablePictureInPicture
                                            controls={false}
                                        />
                                        <div
                                            style={{
                                                padding: '8px',
                                                textAlign: 'center',
                                                fontWeight: 500,
                                                color: isSelected
                                                    ? '#1677ff'
                                                    : undefined,
                                            }}
                                        >
                                            {item.label}
                                            {isSelected && (
                                                <div
                                                    style={{
                                                        fontSize: 12,
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    Đã chọn
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </TabPane>
                </Tabs>
            </Modal>
        </>
    );
};

export default PromptForm;
